import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { find, isNil } from 'lodash-es';

import { ContainerComponent } from '@aw/shared/components/container';
import { CountryFlagComponent } from '@aw/shared/components/country-flag';
import { IconModule } from '@aw/shared/components/icon';
import { CountryPrefix, countryPrefixes } from '@aw/shared/constants';
import { IdPipe } from '@aw/shared/pipes';
import {
  isEmptyString,
  lowercaseIncludes,
  trackByProperty,
} from '@aw/shared/utilities';

import { countryPrefixSelectAnimations } from '../../animations/country-prefix-select.animations';
import { CountryPrefixSelectOptionComponent } from '../country-prefix-select-option/country-prefix-select-option.component';

// TODO: to be able to tab the options in the dropdown after filtering so the value can be selected without the mouse

@Component({
  standalone: true,
  selector: 'aw-country-prefix-select',
  templateUrl: 'country-prefix-select.component.html',
  animations: [countryPrefixSelectAnimations.panelOpen],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    CountryFlagComponent,
    IdPipe,
    OverlayModule,
    IconModule,
    ContainerComponent,
    CountryPrefixSelectOptionComponent,
  ],
})
export class CountryPrefixSelectComponent implements OnInit {
  static nextId = 0;

  @HostBinding('attr.id')
  readonly id =
    `aw-country-prefix-select-${CountryPrefixSelectComponent.nextId++}`;

  @ViewChild('trigger', { static: true })
  readonly trigger!: ElementRef<HTMLButtonElement>;

  @Input()
  set value(_value: string | undefined) {
    this._value = _value;

    this.onValueChange(_value);
  }

  get value(): string | undefined {
    return this._value;
  }

  @Input() panelWidth!: number;

  @Input() disabled = false;

  @Output() valueChange = new EventEmitter<string>();

  items: Array<CountryPrefix> = [];

  selectedCountry?: CountryPrefix;

  panelOpen = false;

  readonly itemHeight = 35;

  readonly maxVisibleItems = 6;

  readonly virtualViewportHeight: number;

  readonly trackByCode = trackByProperty<CountryPrefix>('code');

  readonly positions: Array<ConnectedPosition> = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  constructor(private readonly changeDetector: ChangeDetectorRef) {
    this.virtualViewportHeight = this.itemHeight * this.maxVisibleItems;
  }

  @ViewChild('searchInput')
  private set _(_searchInput: ElementRef<HTMLInputElement> | undefined) {
    if (!isNil(_searchInput)) {
      _searchInput.nativeElement.focus();
    }
  }

  private _value?: string;

  ngOnInit(): void {
    this.items = countryPrefixes;
  }

  onFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    const { value } = input;

    if (isEmptyString(value)) {
      this.resetItemFiltering();
    } else {
      this.items = countryPrefixes.filter(
        (item) =>
          lowercaseIncludes(item.name, value) ||
          lowercaseIncludes(item.prefix, value),
      );

      this.changeDetector.markForCheck();
    }
  }

  onSetPanelOpen(open: boolean): void {
    this.panelOpen = open;

    if (!open) {
      this.resetItemFiltering();
    }

    this.changeDetector.markForCheck();
  }

  onOptionSelected(option: CountryPrefix): void {
    this.valueChange.emit(option.prefix);
    this.value = option.prefix;

    this.onSetPanelOpen(false);
  }

  private onValueChange(prefix?: string): void {
    if (prefix) {
      this.selectedCountry = find(countryPrefixes, {
        prefix,
      });
    } else {
      this.selectedCountry = undefined;
    }

    this.changeDetector.markForCheck();
  }

  private resetItemFiltering(): void {
    this.items = countryPrefixes;
    this.changeDetector.markForCheck();
  }
}
